import React, { useEffect, useMemo, useRef } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { TablePlugin } from '@lexical/react/LexicalTablePlugin'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'

import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { CodeNode } from '@lexical/code'
import { ListItemNode, ListNode } from '@lexical/list'
import { LinkNode } from '@lexical/link'
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table'
import { $convertFromMarkdownString, $convertToMarkdownString, TRANSFORMERS } from '@lexical/markdown'
import type { Transformer } from '@lexical/markdown'
import type { EditorState, LexicalEditor } from 'lexical'
import { LexicalToolbarPlugin } from '@/components/editor/LexicalToolbarPlugin'

import { GFM_TABLE_TRANSFORMER } from '@/services/mappers/lexicalGfmTableTransformer'

type Props = {
  initialMarkdown: string
  placeholder?: string
  onMarkdownChange: (markdown: string) => void
  readOnly?: boolean
  minHeight?: number
  maxHeight?: number
}

function Placeholder({ text }: { text: string }) {
  return (
    <div style={{ color: 'var(--text-muted)', fontSize: '14px', padding: '12px 14px' }}>
      {text}
    </div>
  )
}

export const LexicalMarkdownEditor: React.FC<Props> = ({
  initialMarkdown,
  placeholder = 'Start writing...',
  onMarkdownChange,
  readOnly = false,
  minHeight,
  maxHeight,
}) => {
  const initialMarkdownRef = useRef(initialMarkdown)
  useEffect(() => {
    initialMarkdownRef.current = initialMarkdown
  }, [initialMarkdown])

  const transformers = useMemo<Transformer[]>(() => {
    // Important: Lexical does NOT include table markdown support by default.
    // We add our GFM table transformer to both import/export and markdown shortcuts.
    return [...TRANSFORMERS, GFM_TABLE_TRANSFORMER]
  }, [])

  const initialConfig = useMemo(() => {
    return {
      namespace: 'geo-writer-editor',
      editable: !readOnly,
      nodes: [
        HeadingNode,
        QuoteNode,
        CodeNode,
        ListNode,
        ListItemNode,
        LinkNode,
        TableNode,
        TableRowNode,
        TableCellNode,
      ],
      onError: (error: Error) => {
        console.error('Lexical error:', error)
      },
      editorState: () => {
        $convertFromMarkdownString(initialMarkdownRef.current || '', transformers)
      },
    }
  }, [readOnly, transformers])

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', background: 'var(--surface)' }}>
      <LexicalComposer initialConfig={initialConfig}>
        <EditorShell
          transformers={transformers}
          placeholder={placeholder}
          onMarkdownChange={onMarkdownChange}
          externalMarkdown={initialMarkdown}
          readOnly={readOnly}
          minHeight={minHeight}
          maxHeight={maxHeight}
        />
      </LexicalComposer>
    </div>
  )
}

function EditorShell({
  transformers,
  placeholder,
  onMarkdownChange,
  externalMarkdown,
  readOnly,
  minHeight,
  maxHeight,
}: {
  transformers: Transformer[]
  placeholder: string
  onMarkdownChange: (markdown: string) => void
  externalMarkdown: string
  readOnly: boolean
  minHeight?: number
  maxHeight?: number
}) {
  const [editor] = useLexicalComposerContext()
  const lastAppliedRef = useRef<string | null>(null)
  const isApplyingExternalRef = useRef(false)

  useEffect(() => {
    const next = externalMarkdown ?? ''
    if (lastAppliedRef.current === next) return

    isApplyingExternalRef.current = true
    editor.update(() => {
      $convertFromMarkdownString(next, transformers)
    }, { discrete: true })
    lastAppliedRef.current = next
    // allow OnChange to resume after the import update
    queueMicrotask(() => {
      isApplyingExternalRef.current = false
    })
  }, [editor, externalMarkdown, transformers])

  const handleChange = (editorState: EditorState, _editor: LexicalEditor) => {
    if (isApplyingExternalRef.current) return
    const md = editorState.read(() => $convertToMarkdownString(transformers))
    lastAppliedRef.current = md
    onMarkdownChange(md)
  }

  return (
    <>
      {!readOnly && <LexicalToolbarPlugin />}
      <div style={{ padding: '12px 14px' }}>
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              style={{
                minHeight: `${minHeight ?? 360}px`,
                maxHeight: maxHeight ? `${maxHeight}px` : undefined,
                overflowY: maxHeight ? 'auto' : undefined,
                outline: 'none',
                fontSize: '14px',
                lineHeight: 1.8,
                color: 'var(--text)',
              }}
            />
          }
          placeholder={<Placeholder text={placeholder} />}
          ErrorBoundary={({ children }) => <>{children}</>}
        />
        <HistoryPlugin />
        <LinkPlugin />
        <ListPlugin />
        <TablePlugin />
        {!readOnly && <MarkdownShortcutPlugin transformers={transformers} />}
        {!readOnly && <OnChangePlugin onChange={handleChange} />}
      </div>
    </>
  )
}

